import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { Plus, Archive } from "lucide-react";
import {
  useGetBundles,
  getGetBundlesQueryOptions,
} from "@/requests/gen/react-query/bundle";
import { RequirePermission } from "@/components/universal/require-permission";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import { useQueryStates, type inferParserType } from "nuqs";
import { Toggle } from "@/components/ui/toggle";
import { format, parseISO } from "date-fns";

type BundleSearch = inferParserType<typeof dataTableSearchParsers>;

function bundleQueryParams(
  p: BundleSearch,
): Parameters<typeof getGetBundlesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    entity_ids: p.entity_ids,
  };
}

export const Route = createFileRoute("/_private/_patient-360/bundle/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBundlesQueryOptions(
        bundleQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: BundleLookup,
});

interface BundleTableProps {
  link?: LinkProps;
  name: string;
  entity: string;
  created_at: string;
}

const columns: ColumnDef<BundleTableProps>[] = [
  {
    header: "Name",
    accessorKey: "name",
  },
  {
    header: "Entity",
    accessorKey: "entity",
  },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
    enableSorting: true,
  },
];

function BundleLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);

  const bundles = useGetBundles(bundleQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const tableData: BundleTableProps[] | undefined = bundles.data?.items.map(
    (bundle) => {
      return {
        link: linkOptions({
          to: "/bundle/$bundleId",
          params: { bundleId: String(bundle.bundle_id) },
        }),
        name: bundle.name,
        entity: bundle.entity.name,
        created_at: bundle.created_at,
      };
    },
  );

  return (
    <div>
      <title>Bundles - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Bundles", link: { to: "/bundle/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Bundles</CardTitle>
          <CardDescription>
            View your bundles below. You can search, filter, and click to see
            more details.
          </CardDescription>
          <CardAction>
            <RequirePermission permission="patient_360">
              <Button variant="default" size="sm" asChild>
                <Link to="/bundle/create">
                  <Plus />
                  Create Bundle
                </Link>
              </Button>
            </RequirePermission>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bundles.isFetching} />
            <DropdownFilter
              title="Entity"
              options={[
                {
                  items:
                    entities.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => {
                        return {
                          label: entity.name,
                          value: entity.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              search={entitySearch}
              setSearch={setEntitySearch}
              isFetching={entities.isFetchingNextPage}
              fetchNextPage={entities.fetchNextPage}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
            />
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) => {
                setQueryStates({
                  is_deleted: pressed,
                });
              }}
            >
              <Archive />
              Archived
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bundles.isLoading}
            isError={bundles.isError}
            count={bundles.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
